// ------------------------------------------------------------
// Kincode Button

import { useState, useEffect } from 'react';

import { getKincodeButtonThemeFromHex } from '@/utils/companyBrand';

// ------------------------------------------------------------
const KincodeButton = ({
  children,
  onClick,
  id = '',
  disabled = false,
  className = '',
  theme,
  variant = 'primary', // 'primary' or 'secondary'
  colorHex = '#BBF7D0', // Hex color for fill, border, and auto theme
  ariaLabel, // Accessible label for screen readers
  type = 'button',
  ...props
}) => {
  const baseStyles = `
    flex flex-row items-center justify-center gap-2 
    h-11 px-6 rounded-kcPill text-sm font-medium cursor-pointer min-w-0
    disabled:cursor-not-allowed disabled:text-kc-black disabled:bg-[#828282] disabled:before:opacity-0 disabled:after:opacity-0 
    hover:scale-[1.015] 
    focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-purple-600 
  `;

  let newStyles = '';

  const [buttonStyles, setButtonStyles] = useState('');

  const resolvedTheme = theme ?? getKincodeButtonThemeFromHex(colorHex);
  const btnType = `${variant} ${resolvedTheme}`;

  useEffect(() => {
    switch (btnType) {
      case 'primary light':
        newStyles = ` 
          text-kc-black dark:text-kc-gray-dark shadow-kcLightButtons dark:shadow-kcDarkButtons
          enabled:hover:brightness-110 
          enabled:active:brightness-90 
        `;
        break;

      case 'secondary light':
        newStyles = ` 
          text-kc-black dark:text-kc-gray-dark shadow-kcLightButtons dark:shadow-kcDarkButtons
          border-2 bg-white
          enabled:hover:brightness-95 
          enabled:active:brightness-85
        `;
        break;

      case 'primary dark':
        newStyles = ` 
          text-white dark:text-kc-gray-light shadow-kcDarkButtons dark:shadow-kcLightButtons
          enabled:hover:brightness-110 
          enabled:active:brightness-90 
        `;
        break;

      case 'secondary dark':
        newStyles = ` 
          text-kc-black dark:text-kc-gray-light shadow-kcDarkButtons dark:shadow-kcLightButtons
          border-2 bg-transparent
          enabled:hover:brightness-95 
          enabled:active:brightness-85
        `;
        break;

      case 'tertiary light':
      case 'tertiary dark':
        newStyles = ` 
          text-kc-black dark:text-kc-gray-light text-left relative px-0 rounded-none shadow-none underline 
          disabled:bg-transparent disabled:text-kc-black disabled:cursor-not-allowed
        `;
        break;

      default:
        break;
    }

    setButtonStyles(newStyles);
  }, [colorHex, variant, resolvedTheme]);

  const brandStyle =
    disabled || variant !== 'primary'
      ? {}
      : {
          backgroundColor: colorHex,
        };
  const secondaryBorderStyle =
    disabled || variant !== 'secondary' ? {} : { borderColor: colorHex };

  return (
    <button
      type={type}
      id={id}
      onClick={onClick}
      disabled={disabled}
      style={{ ...brandStyle, ...secondaryBorderStyle }}
      className={`${baseStyles} ${buttonStyles} ${className}`}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      {...props}
    >
      <span className="inline-flex max-w-full items-center justify-center gap-2 whitespace-nowrap [&_svg]:shrink-0">
        {children}
      </span>
    </button>
  );
};

export default KincodeButton;
export { KincodeButton };
